import { TFunction } from 'i18next';
import * as MSAL from '@azure/msal-browser';

import { ReduxThunkType } from '../../types/ReduxThunkType';
import { Actions as AuthActions } from './actions';
import { tokenRequest } from '../../constants/login/loginConfig';
import { msalInstance } from '../../constants/login/msalInstance';
import { history } from '../history';
import { LOGIN_ROUTE } from '../../constants/routes';
import { RestErrorResponse } from '../../types/response/RestErrorResponse';
import AuthService from '../../services/AccountService';
import { AuthResponse, LoginFormData } from './types';
import { spawnGlobalNotification } from '../global-notifications/asyncActions';
import { createErrorNotificationContent } from '../../../pages/login/LoginErrorMessage';

export const login = (): ReduxThunkType<void> => async (dispatch) => {
  try {
    dispatch(AuthActions.login());
    await msalInstance.loginRedirect(tokenRequest);
    dispatch(AuthActions.loginSuccess());
  } catch (error) {
    dispatch(AuthActions.loginError(error as RestErrorResponse));
  }
};

export const silentLogin = (): ReduxThunkType<void> => async (dispatch) => {
  try {
    dispatch(AuthActions.silentLogin());
    const accounts = msalInstance.getAllAccounts();
    const request = {
      ...tokenRequest,
      account: accounts[0]
    };
    const response = await msalInstance.acquireTokenSilent(request);
    dispatch(AuthActions.silentLoginSuccess(response as AuthResponse));
  } catch (error: any) {
    dispatch(AuthActions.silentLoginError(error as RestErrorResponse));

    if (
      error instanceof MSAL.InteractionRequiredAuthError ||
      error?.errorCode === 'invalid_grant' ||
      error?.suberror === 'bad_token' ||
      (Array.isArray(error?.errorCodes) && error.errorCodes.includes(700084))
    ) {
      sessionStorage.removeItem('CSFR_TOKEN');
    }

    dispatch(AuthActions.setNotAuthenticated());
    history.push(LOGIN_ROUTE);
  }
};

export const silentLoginExternal = (): ReduxThunkType<void> => async (dispatch) => {
  try {
    dispatch(AuthActions.silentLogin());
    const response = await AuthService.silentLoginExternal();
    dispatch(AuthActions.silentLoginSuccess(response.data.content));
  } catch (error) {
    dispatch(AuthActions.silentLoginError(error as RestErrorResponse));
    history.push(LOGIN_ROUTE);
  }
};

export const logout = (): ReduxThunkType<void> => async (dispatch) => {
  try {
    dispatch(AuthActions.logout());
    await msalInstance.logoutRedirect();
    dispatch(AuthActions.logoutSuccess());
  } catch (error) {
    dispatch(AuthActions.logoutError(error as RestErrorResponse));
  }
};

export const logoutExternal = (): ReduxThunkType<void> => async (dispatch) => {
  try {
    dispatch(AuthActions.logout());
    sessionStorage.setItem('CSFR_TOKEN', '');
    dispatch(AuthActions.logoutSuccess());
    history.push(LOGIN_ROUTE);
  } catch (error) {
    dispatch(AuthActions.logoutError(error as RestErrorResponse));
  }
};

export const loginExternal =
  (data: LoginFormData, t: TFunction): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(AuthActions.login());
      const response = await AuthService.loginExternal(data);
      if (!response.data.result.result) {
        dispatch(spawnGlobalNotification(createErrorNotificationContent(response, t)));
      }
      dispatch(AuthActions.loginExternalSuccess(response.data.content));
      if (response.data.content.accessToken) {
        sessionStorage.setItem('CSFR_TOKEN', response.data.content.accessToken);
        history.push(`/projects/${response.data.content.account.projectID}/bookings`);
      }
    } catch (error) {
      dispatch(AuthActions.loginError(error as RestErrorResponse));
    }
  };
